import { Avatar, Button, message, Table, TableColumnsType } from 'antd'
import { StakingNftType, TokenizeCollectionType } from 'Modules'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ProtectedButton from 'component/button/protected-button'
import ButtonCopy from 'component/button/button-copy'
import LinkAddress from 'component/link/link-address'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { FEATURE } from 'constants/feature'
import { StakingNFTPoolType } from 'constants/staking-nft'
import { useStakeNFTMeyReward } from 'hooks/stake-nft/use-stake-nft-mey-reward'
import { useStakeNFTUsdcReward } from 'hooks/stake-nft/use-stake-nft-usdc-reward'
import useSearchParams from 'hooks/use-search-params'
import Overview from 'modules/stake-nft/listing/overview'
import { ROUTES } from 'routes'
import { logInfo } from 'utils/logger'

import ModalAddNftCollection from './modal-add-nft-collection'

import 'pages/staking-nft/style.scss'

const ListPTOStakingNFT = () => {
  const navigate = useNavigate()

  const location = useParams()

  const stakingNFTId = location.id

  const [stakingNft, setStakingNft] = useState<StakingNftType | null>(null)

  const [nftCollections, setNftCollections] = useState<TokenizeCollectionType[]>([])

  const [total, setTotal] = useState(0)

  const [messageApi, contextHolder] = message.useMessage()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const [nftCollection, setNftCollection] = useState<TokenizeCollectionType | null>(null)

  const { searchParams, initPagination, onChangePagination } = useSearchParams()
  const { isContractManager: isStakeNftMeyManager } = useStakeNFTMeyReward(
    stakingNft?.contractAddress
  )
  const { isContractManager: isStakeNftUsdcManager } = useStakeNFTUsdcReward(
    stakingNft?.contractAddress
  )

  const getStakingNft = useCallback(async () => {
    try {
      if (stakingNFTId) {
        const res = await AppAdmin.getDetailStakingTokenize(stakingNFTId)
        if (res?.data.success === true) {
          setStakingNft(res?.data.data)
        }
      }
    } catch (error) {
      logInfo('Error: ', error)
    }
  }, [stakingNFTId])

  const getListNftCollection = useCallback(async () => {
    try {
      if (stakingNFTId) {
        const res = await AppAdmin.getListNftCollection({
          ...searchParams,
          stakingTokenize: stakingNFTId,
        })
        if (res?.data.success === true) {
          setNftCollections(res?.data?.data.results || [])
          setTotal(res?.data?.data?.totalResults)
        }
      }
    } catch (error) {
      logInfo('Error: ', error)
    }
  }, [searchParams, stakingNFTId])

  const handleClickCreate = () => {
    setNftCollection(null)
    setIsModalOpen(true)
  }

  const handleClickEdit = (nftCollection: TokenizeCollectionType) => {
    setNftCollection(nftCollection)
    setIsModalOpen(true)
  }

  useEffect(() => {
    getStakingNft()
    getListNftCollection()
  }, [getListNftCollection, getStakingNft, stakingNFTId])

  const columns: TableColumnsType<TokenizeCollectionType> = [
    {
      title: 'Logo',
      dataIndex: 'thumbnail',
      key: 'thumbnail',
      render: (_, record) => <Avatar src={record?.tokenize?.thumbnail} size={50} />,
    },
    {
      title: 'Name',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (_, record) => <span>{record?.tokenize?.fullName}</span>,
    },
    {
      title: 'Contract Address',
      dataIndex: 'contractAddress',
      key: 'contractAddress',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LinkAddress address={record?.tokenize?.contractAddress} />
          {record?.tokenize?.contractAddress ? (
            <ButtonCopy text={record.tokenize.contractAddress} />
          ) : null}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (_, record) =>
        record?.isActive ? (
          <span className="status-success">Active</span>
        ) : (
          <span className="status-fail">Unactive</span>
        ),
    },
    {
      title: 'Actions',
      dataIndex: 'Actions',
      key: 'detail',
      render: (_, record) => (
        <div className="btn-group">
          <Button
            className="button-detail"
            onClick={() => {
              navigate(`${ROUTES.PTO}/${record.tokenize._id}`)
            }}
          >
            View
          </Button>
          <ProtectedButton
            type="default"
            className="button-edit"
            feature={FEATURE.STAKING_NFT}
            requireCRUD
            onClick={() => handleClickEdit(record)}
          >
            Edit
          </ProtectedButton>
          <ProtectedButton
            feature={FEATURE.STAKING_NFT} requireCRUD
            onClick={() => {
              navigate(`${ROUTES.STAKING_NFT}/${stakingNFTId}${ROUTES.PTO}/${record.tokenize._id}`)
            }}
          >
            Transaction
          </ProtectedButton>
        </div>
      ),
    },
  ]

  const ableAddNftCollection =
    (stakingNft?.name === StakingNFTPoolType.MEY && isStakeNftMeyManager) ||
    (stakingNft?.name === StakingNFTPoolType.USDC && isStakeNftUsdcManager)

  return (
    <>
      {contextHolder}
      <div className="main-section staking">
        <Breadcrumb additionalItems={[BREADCRUMBS.STAKING_TOKEN, BREADCRUMBS.STAKING_NFT_PTO]} />
        <div className="dashboard-container">
          <Overview id={stakingNFTId} />
        </div>
        <div className="main-title">
          <span>List PTO (Rewards by {stakingNft?.name})</span>
          {ableAddNftCollection ? (
            <span className="new-button">
              <ProtectedButton
                type="primary"
                feature={FEATURE.STAKING_NFT} requireCRUD
                onClick={handleClickCreate}
              >
                Add NFT Collection
              </ProtectedButton>
            </span>
          ) : null}
        </div>

        <div className="table-row">
          <div className="main-table-row">
            <Table<TokenizeCollectionType>
              columns={columns}
              rowKey={(record) => record._id}
              dataSource={nftCollections || []}
              className="main-table"
              scroll={{ x: 1000 }}
              pagination={initPagination(total)}
              onChange={onChangePagination}
            />
            {stakingNFTId && (
              <ModalAddNftCollection
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                stakingTokenize={stakingNFTId}
                messageApi={messageApi}
                onSubmit={getListNftCollection}
                stakingNft={stakingNft}
                nftCollection={nftCollection}
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}
export default ListPTOStakingNFT
